
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";
import { fetchFoundationalTasks, deleteFoundationalTask } from "./foundationalTasksService";
import { fetchGrowthTasks, deleteGrowthTask } from "./growthTasksService";
import { fetchRecurringTasks, deleteRecurringTask } from "./recurringTasksService";
import { FoundationalTask, GrowthTask, RecurringTask } from "./types";

export const useMyTasks = () => {
  const [foundationalTasks, setFoundationalTasks] = useState<FoundationalTask[]>([]);
  const [growthTasks, setGrowthTasks] = useState<GrowthTask[]>([]);
  const [recurringTasks, setRecurringTasks] = useState<RecurringTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();

  const handleFoundationalTasks = async () => {
    if (!user || !currentBrandId) {
      setFoundationalTasks([]);
      return;
    }

    try {
      const tasks = await fetchFoundationalTasks(user.id, currentBrandId);
      setFoundationalTasks(tasks);
    } catch (error: any) {
      console.error('Error fetching foundational tasks:', error);
      toast({
        title: "Error",
        description: "Failed to fetch foundational tasks",
        variant: "destructive",
      });
    }
  };

  const handleGrowthTasks = async () => {
    if (!user || !currentBrandId) {
      setGrowthTasks([]);
      return;
    }

    try {
      const tasks = await fetchGrowthTasks(user.id, currentBrandId);
      setGrowthTasks(tasks);
    } catch (error: any) {
      console.error('Error fetching growth tasks:', error);
      toast({
        title: "Error",
        description: "Failed to fetch growth tasks",
        variant: "destructive",
      });
    }
  };

  const handleRecurringTasks = async () => {
    if (!user || !currentBrandId) {
      setRecurringTasks([]);
      return;
    }

    try {
      const tasks = await fetchRecurringTasks(user.id, currentBrandId);
      setRecurringTasks(tasks);
    } catch (error: any) {
      console.error('Error fetching recurring tasks:', error);
      toast({
        title: "Error",
        description: "Failed to fetch recurring tasks",
        variant: "destructive",
      });
    }
  };

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      await Promise.all([handleFoundationalTasks(), handleGrowthTasks(), handleRecurringTasks()]);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTask = async (taskId: string, taskType: 'foundational' | 'growth' | 'recurring') => {
    try {
      if (taskType === 'foundational') {
        await deleteFoundationalTask(taskId);
        setFoundationalTasks(prev => prev.filter(task => task.id !== taskId));
      } else if (taskType === 'growth') {
        await deleteGrowthTask(taskId);
        setGrowthTasks(prev => prev.filter(task => task.id !== taskId));
      } else {
        await deleteRecurringTask(taskId);
        setRecurringTasks(prev => prev.filter(task => task.id !== taskId));
      }
      
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    } catch (error: any) {
      console.error('Error deleting task:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [user, currentBrandId]);

  return {
    foundationalTasks,
    growthTasks,
    recurringTasks,
    isLoading,
    refetch: fetchTasks,
    deleteTask,
  };
};

// Export types for use in other files
export type { FoundationalTask, GrowthTask, RecurringTask } from "./types";
